import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { Header } from '@components/Header';
import Done from '@components/icons/Done.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { Tabs } from '@pages/Admin/AttorneysAccessPage/components/Tabs';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { SwitchWrapper } from '@pages/User/BillingAccountDetailsPage/components/AutoTopUpCard/styles';
import { FlexCellContent } from '@pages/User/MemberReportsPage/styles/FlexCellContent';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminService, PaginatedAttorneyAccessResponseDto } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { attorneysKeys, TAttorneysListParams } from 'app/queries/attorneys/attorneysKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useMemo } from 'react';
import { createFetchAttorneyAccessesQueryFn } from '../../api/fetchAttorneyAccesses';
import { CellContent } from './styles/CellContent';
import { Wrapper } from './styles/Wrapper';

const SEARCH_DEBOUNCE_MS = 300;

export const Content = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalContext();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
  });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TAttorneysListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const accessesQuery = useQuery({
    queryKey: attorneysKeys.accessList(listParams),
    queryFn: createFetchAttorneyAccessesQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  const verifyMutation = useMutation({
    mutationFn: (email: string) => call(() => AdminService.verifyAttorney({ email })),
    onSettled: () => queryClient.invalidateQueries({ queryKey: attorneysKeys.all }),
  });

  const toggleAccessMutation = useMutation({
    mutationFn: ({ attorneyAccessId, disabledByAdmin }: { attorneyAccessId: string; disabledByAdmin: boolean }) =>
      call(() =>
        disabledByAdmin
          ? AdminService.enableAttorneyAccess({ attorneyAccessId })
          : AdminService.disableAttorneyAccess({ attorneyAccessId }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: attorneysKeys.accessLists() }),
  });

  const verify = (email: string) => {
    openModal(ModalNamesEnum.GenericConfirmation, {
      title: 'Confirm',
      description: `Do you really want to verify this user (${email})?`,
      buttonText: 'Verify',
      onSuccess: () => {
        closeModal();
        verifyMutation.mutate(email);
      },
    });
  };

  const openAddNewAttorneyAccessModal = () => {
    openModal(ModalNamesEnum.AdminAddNewAttorneysAccess, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: attorneysKeys.accessLists() });
      },
    });
  };

  const attorneyAccessesList = accessesQuery.data ?? { data: [], count: 0 };
  const isLoading =
    accessesQuery.isFetching || isSearchSettling || verifyMutation.isPending || toggleAccessMutation.isPending;

  return (
    <>
      <Header
        title="Attorney Access"
        buttonLabel={'Add new access'}
        buttonOnClick={openAddNewAttorneyAccessModal}
        setSearch={setSearch}
        search={search}
        leftSideContent={[<Tabs key="PageTabs" currentTab="Invitations" />]}
      />

      <LinearProgressLoader active={isLoading} />
      <Wrapper>
        <TableWrapper>
          <Table>
            <Thead>
              <Tr>
                <Th>#</Th>
                <Th>Attorney email</Th>
                <Th>Recording session id</Th>
                <Th>Job id</Th>
                <Th>Online</Th>
                <Th>Registered</Th>
                <Th>Verified</Th>
                <Th>Disabled by admin</Th>
                <Th>Created</Th>
              </Tr>
            </Thead>
            <Tbody>
              {attorneyAccessesList.data?.map((data: PaginatedAttorneyAccessResponseDto['data'][0], index: number) => (
                <Tr key={data.id}>
                  <Td>
                    <CellContent>{index + 1}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.email}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.recordingSessionId}</CellContent>
                  </Td>
                  <Td>
                    <CellContent>{data.RecordingSession.jobId}</CellContent>
                  </Td>
                  <Td>
                    <CellContent className="text-center">
                      {data.connected ? (
                        <svg
                          aria-hidden="true"
                          display="block"
                          width="15"
                          height="15"
                          style={{ width: '15px', height: '15px' }}
                          viewBox="0 0 12 12"
                          fill="none"
                        >
                          <path fill="#4bb34b" d="M10 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0"></path>
                        </svg>
                      ) : null}
                    </CellContent>
                  </Td>
                  <Td>
                    <CellContent className="text-center">{data.registered ? <Done /> : null}</CellContent>
                  </Td>
                  <Td>
                    <CellContent className="text-center">
                      {data.emailVerified ? (
                        <Done />
                      ) : data.registered ? (
                        <OutlinedButton className="verify-button" onClick={() => verify(data.email)}>
                          Verify
                        </OutlinedButton>
                      ) : null}
                    </CellContent>
                  </Td>
                  <Td>
                    <FlexCellContent>
                      <SwitchWrapper>
                        <Switch
                          checked={data.disabledByAdmin}
                          onChange={() =>
                            toggleAccessMutation.mutate({
                              attorneyAccessId: data.id,
                              disabledByAdmin: data.disabledByAdmin,
                            })
                          }
                        />
                      </SwitchWrapper>
                    </FlexCellContent>
                  </Td>
                  <Td>
                    <CellContent>{getAmericanTime(data.createdAt)}</CellContent>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableWrapper>
        <PagePagination
          totalItemsCount={attorneyAccessesList?.count ?? 0}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </Wrapper>
    </>
  );
};
